// Post-Fast Insights Toast — logic module.
//
// Fires a persistent, non-modal toast 20s after the first meal of the day is
// logged, but only when the fast that just ended is strictly > 9h. Persists
// each qualifying fast as a structured row in `hayahFastEvents` so HayahAICoach
// can read history via the read API below and mutate via the exported helpers
// (setUserReflection / addTags / removeTags / regenerateInsight) — never by
// touching localStorage directly, so the schema contract stays in one file.
//
// See /Users/yusufdockrat/.claude/plans/read-hayah-md-first-to-hazy-flask.md
// for the full design.

import { useEffect, useRef, useState, useCallback } from "react";
import { computeFastingWindow, loadFastingOverrides, loadPrefs, localDateStr } from "./hayahPrefs";
import { callGemini, getGeminiKey } from "./geminiAI";
import { ISLAMIC_VOICE, OUTPUT_JSON_STRICT, composeSystem } from "./hayah/hayahPrompts";
import { getUserProfile, formatProfileForPrompt } from "./hayah/userProfile";
import { MODELS } from "./hayah/aiConfig";

// ── localStorage keys ─────────────────────────────────────────────
const EVENTS_KEY  = "hayahFastEvents";          // structured fast history (read by AI Coach)
const PENDING_KEY = "hayahPendingFastToast";    // pre-fire scheduled toast (UI only)
const SHOWN_KEY   = "hayahShownFastToasts";     // ensures once-per-fast (UI only)

const EVENTS_CAP = 180;                          // ≈6 months
const SHOWN_CAP  = 200;
const TOAST_DELAY_MS  = 20_000;
const GEMINI_TIMEOUT_MS = 15_000;
const FRESHNESS_MS = 60_000;
// Must stay in sync with CalorieCounter MEAL_SLOTS keys.
const MEAL_SLOTS = ["meal", "drinks", "supplements", "whey", "snacks"];

// ── Custom events ─────────────────────────────────────────────────
const EVT_RECORDED = "sirat:fastRecorded";   // data event (AI Coach subscribes)
const EVT_TOAST    = "sirat:fastToast";      // UI event (scheduler subscribes)
const EVT_UPDATED  = "sirat:fastUpdated";    // post-regenerateInsight refresh

// ── Tiers ─────────────────────────────────────────────────────────
// `min` in minutes. A fast at exactly `min` qualifies for the tier.
const TIERS = [
  { slug: "glycogen",       min:  9 * 60, max: 12 * 60, title: "9-12h · Blood sugar stabilises",     points: [
    "Insulin levels drop",
    "Body shifts to stored glucose",
    "Energy stabilises after the early-day dip",
  ]},
  { slug: "ketosis",         min: 12 * 60, max: 14 * 60, title: "12-14h · Glycogen depletion",        points: [
    "Liver glycogen runs out",
    "Fat burning kicks in",
    "Mood-stabilising ketones begin",
  ]},
  { slug: "autophagy",       min: 14 * 60, max: 16 * 60, title: "14-16h · Ketosis & clarity",         points: [
    "Ketones climb steadily",
    "Growth hormone rises",
    "Mental clarity boost",
  ]},
  { slug: "hgh",             min: 16 * 60, max: 18 * 60, title: "16-18h · Autophagy onset",           points: [
    "Cellular cleanup initiates",
    "Inflammation markers drop",
    "Cells recycle damaged proteins",
  ]},
  { slug: "deep_autophagy",  min: 18 * 60, max: 24 * 60, title: "18-24h · Deep autophagy",            points: [
    "Stem-cell regeneration signals",
    "Enhanced metabolic flexibility",
    "Strongest cellular renewal window",
  ]},
];

// ───────────────────────────────────────────────────────────────────
// Read API — used by HayahAICoach + the overlay.
// ───────────────────────────────────────────────────────────────────

function readEvents() {
  try {
    const raw = JSON.parse(localStorage.getItem(EVENTS_KEY) || "[]");
    if (!Array.isArray(raw)) return [];
    // Skip rows missing the schema_version contract (v1 forward-compat).
    return raw.filter(e => e && typeof e === "object" && (e.schema_version | 0) >= 1);
  } catch { return []; }
}

function writeEvents(events) {
  try {
    const capped = events.slice(0, EVENTS_CAP);
    localStorage.setItem(EVENTS_KEY, JSON.stringify(capped));
  } catch {}
}

export function getRecentFasts(limit = 30) {
  return readEvents()
    .slice()
    .sort((a, b) => (b.end_ts || 0) - (a.end_ts || 0))
    .slice(0, Math.max(0, limit | 0));
}

export function getFastById(fastId) {
  if (!fastId) return null;
  return readEvents().find(e => e.id === fastId) || null;
}

export function getLatestFast() {
  return getRecentFasts(1)[0] || null;
}

// Linear-regression slope over the last 14d of qualifying fasts.
// y = duration_min, x = chronological index (oldest = 0).
function regressionSlope(durations) {
  const n = durations.length;
  if (n < 2) return 0;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  for (let i = 0; i < n; i++) {
    sumX += i; sumY += durations[i];
    sumXY += i * durations[i]; sumX2 += i * i;
  }
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return 0;
  return (n * sumXY - sumX * sumY) / denom;
}

function mean(arr) {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

export function getFastStats(daysBack = 30) {
  const cutoff = Date.now() - daysBack * 24 * 3600 * 1000;
  const all = readEvents().filter(e => (e.end_ts || 0) >= cutoff);
  const durations = all.map(e => +e.duration_min || 0).filter(d => d > 9 * 60);

  // 14d window for trend (chronologically ordered, oldest first).
  const trendCutoff = Date.now() - 14 * 24 * 3600 * 1000;
  const last14 = all
    .filter(e => (e.end_ts || 0) >= trendCutoff)
    .sort((a, b) => (a.end_ts || 0) - (b.end_ts || 0))
    .map(e => +e.duration_min || 0)
    .filter(d => d > 9 * 60);

  const slope = regressionSlope(last14);
  let trend = "stable";
  if (slope > 2) trend = "improving";
  else if (slope < -2) trend = "declining";

  // 7d avg for the summary string.
  const sevenCutoff = Date.now() - 7 * 24 * 3600 * 1000;
  const last7 = all
    .filter(e => (e.end_ts || 0) >= sevenCutoff)
    .map(e => +e.duration_min || 0)
    .filter(d => d > 9 * 60);

  return {
    avgDurationMin:        durations.length ? Math.round(mean(durations))      : 0,
    longestMin:            durations.length ? Math.max(...durations)            : 0,
    shortestMin:           durations.length ? Math.min(...durations)            : 0,
    totalQualifyingFasts:  durations.length,
    last7DayAvg:           last7.length      ? Math.round(mean(last7))          : 0,
    trend,
    _slopePerFast: slope,
  };
}

function fmtDuration(min) {
  if (!Number.isFinite(min) || min <= 0) return "0h 00m";
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

// Single-line summary for AI Coach system prompts.
export function summarizeFastsForAI(daysBack = 14) {
  const cutoff      = Date.now() - daysBack * 24 * 3600 * 1000;
  const priorCutoff = Date.now() - 2 * daysBack * 24 * 3600 * 1000;
  const events = readEvents();
  const recent = events.filter(e => (e.end_ts || 0) >= cutoff);
  const prior  = events.filter(e => (e.end_ts || 0) >= priorCutoff && (e.end_ts || 0) < cutoff);

  const recentDur = recent.map(e => +e.duration_min || 0).filter(d => d > 9 * 60);
  const priorDur  = prior.map(e => +e.duration_min || 0).filter(d => d > 9 * 60);

  if (!recentDur.length) return `Last ${daysBack} days: no qualifying fasts logged.`;

  const avg     = Math.round(mean(recentDur));
  const longest = Math.max(...recentDur);
  const autophagyCount = recent.filter(e => Array.isArray(e.tiers_reached) && e.tiers_reached.includes("autophagy")).length;

  let trendPart = "trend insufficient data";
  if (priorDur.length) {
    const delta = Math.round(mean(recentDur) - mean(priorDur));
    if (delta > 5)        trendPart = `trend improving (+${delta} min vs prior ${daysBack}d)`;
    else if (delta < -5)  trendPart = `trend declining (${delta} min vs prior ${daysBack}d)`;
    else                  trendPart = `trend stable (${delta >= 0 ? "+" : ""}${delta} min vs prior ${daysBack}d)`;
  }

  return `Last ${daysBack} days: ${recentDur.length} qualifying fasts, avg ${fmtDuration(avg)}, longest ${fmtDuration(longest)}, ${autophagyCount} hit autophagy tier, ${trendPart}.`;
}

// ───────────────────────────────────────────────────────────────────
// Pure helpers — exported for AI Coach.
// ───────────────────────────────────────────────────────────────────

export function healthTiersAchieved(durationMin) {
  if (!Number.isFinite(durationMin) || durationMin <= 0) return [];
  return TIERS.filter(t => durationMin >= t.min);
}

function tierSlugsReached(durationMin) {
  return healthTiersAchieved(durationMin).map(t => t.slug);
}

function highestTierSlug(durationMin) {
  const reached = healthTiersAchieved(durationMin);
  return reached.length ? reached[reached.length - 1].slug : null;
}

// Rule-based 4-bullet insight. Accepts any stored row (no live closures).
export function buildRuleInsight(fastEvent, recentDurationsMin) {
  const breaking = fastEvent?.breaking_meal || {};
  const durationMin = +fastEvent?.duration_min || 0;
  const tiers = healthTiersAchieved(durationMin).map(t => t.slug);

  const sugar    = +breaking.sugar    || 0;
  const protein  = +breaking.protein  || 0;
  const category = String(breaking.category || "");
  const source   = String(breaking.source   || "");
  const slot     = String(breaking.slot     || "");
  const isJunk = sugar > 20 || category === "Snacks" || slot === "snacks" || source === "sa_restaurant";
  const isSunnah = source === "sunnah";
  const isBalanced = protein > 15 && sugar < 10;

  // 1. Quality of the breaking meal.
  let b1;
  if (isJunk)          b1 = "Heavy or sugary break — spikes insulin and softens autophagy gains.";
  else if (isSunnah)   b1 = "Sunnah break — slow-release energy, anti-inflammatory, keeps glucose steady.";
  else if (isBalanced) b1 = "Solid break — protein-led, low sugar lets the body transition steadily.";
  else                 b1 = "Steady break — neither spike nor power-up; lean protein next time would compound benefits.";

  // 2. Trend vs recent fasts.
  const recent = (recentDurationsMin || []).filter(d => Number.isFinite(d) && d > 9 * 60);
  let b2;
  if (recent.length < 3) {
    b2 = "Building your baseline — log a few more fasts to see your trend.";
  } else {
    const avg = mean(recent);
    if (durationMin > avg + 30)      b2 = `Longer than your recent avg of ${fmtDuration(Math.round(avg))} — momentum.`;
    else if (durationMin < avg - 30) b2 = `Shorter than usual — your body adapted, push the next one.`;
    else                             b2 = "Consistent with your recent rhythm — habit is forming.";
  }

  // 3. Tip for the rest of today.
  let b3;
  if (sugar > 20)                                                              b3 = "Hydrate well and skip sugary drinks today to lock in glucose stability.";
  else if (tiers.includes("hgh") || tiers.includes("deep_autophagy"))         b3 = "Stretch the benefits — protein + healthy fats next, no snacks before dinner.";
  else                                                                         b3 = "Hydrate now and aim for 3-4 hours before your next meal.";

  // 4. Suggestion for the next fast.
  let b4;
  if (isJunk)                                                                  b4 = "Try breaking with Sunnah foods next time — dates, honey, olive oil.";
  else if (durationMin >= 16 * 60)                                             b4 = "Hold this window — long fasts amplify benefits when consistent.";
  else if (recent.length >= 3 && durationMin > mean(recent))                   b4 = "Stretch tomorrow's fast by 30 minutes — you're trending well.";
  else                                                                         b4 = "Match this duration tomorrow to build the habit.";

  return [b1, b2, b3, b4].map(s => `• ${s}`).join("\n");
}

// ───────────────────────────────────────────────────────────────────
// Gemini path — module-level promise registry so dismissal does not
// cancel the in-flight call. Result writes back to hayahFastEvents
// even if the overlay has already unmounted.
// ───────────────────────────────────────────────────────────────────

const pendingGeminiCalls = new Map(); // fast_id -> Promise<string>

function withTimeout(promise, ms) {
  let to;
  const timeout = new Promise((_, reject) => {
    to = setTimeout(() => reject(new Error("timeout")), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(to));
}

// v2: ISLAMIC_VOICE persona + schema-locked 4-bullet output. The break-day
// context now includes sleep/vitals (recovery state when breaking), today's
// running macro intake (so the suggestion can land on the right macro
// nudge), today's workout if any (changes fuel priorities), and hydration
// during the fast (water cups).
function geminiSystemInstruction() {
  return composeSystem([
    "You are a precision nutrition and metabolic health coach. Voice is direct, evidence-based, body-first. Reference the user's actual numbers — duration, macros, trend deltas, calorie position vs target. No fluff, no generic advice, no preamble. Every bullet must tell the user something they can act on or feel in their body.",
    formatProfileForPrompt(getUserProfile()),
    OUTPUT_JSON_STRICT,
    `Task: Return JSON { bullets: [4 strings] }. Bullets cover, in order:

(1) Metabolic verdict on the meal that broke the fast — name the specific physiological consequence (insulin spike magnitude, autophagy interruption, glucose curve, satiety signal). Reference real macros from the entry. If the meal undermined the fast's gains, say so plainly.

(2) Trend vs recentFasts — improving / stable / declining with specific delta in minutes or hours. Tie the trend to a body outcome (deeper ketosis tolerance, improving fat adaptation, regression toward baseline).

(3) One tip for the rest of today — drawn from macros consumed so far, calorie position vs target, hydration, and protein leverage. Name the next decision (next meal timing, protein floor, carb cap, electrolyte action).

(4) One suggestion for the next fast — concrete duration target or break-fast composition that compounds today's gain. Tie to a physiological milestone the user is close to unlocking (e.g. "push to 16h to enter autophagy" / "break with 30g protein first to blunt the glucose spike").

Constraints: Each bullet max 22 words. Specific not generic. Use numbers from the data. No motivational filler. No hedging language ("might", "could help"). State the mechanism, state the action.`,
  ]);
}

function buildFastBreakContext(fastEvent) {
  const profile = getUserProfile();
  let allData = {};
  try {
    // Inline minimal readAllData rather than import to avoid a circular path.
    const today = localDateStr();
    allData = {
      today,
      calorieLog: safeJSON("calorieLog", {}),
      calorieGoals: safeJSON("calorieGoals", {}),
      workouts: safeJSON("sirat-workouts", []),
    };
  } catch {}
  const todayLog = allData.calorieLog?.[allData.today] || {};
  const items = Object.values(todayLog.meals || {}).flat();
  const cumCal  = Math.round(items.reduce((s, e) => s + (e?.calories || 0), 0));
  const cumProt = Math.round(items.reduce((s, e) => s + (e?.protein || 0), 0));
  const cumCarb = Math.round(items.reduce((s, e) => s + (e?.carbs || 0), 0));
  const cumFat  = Math.round(items.reduce((s, e) => s + (e?.fat || 0), 0));
  const todayWorkout = (allData.workouts || []).find(w => {
    const ts = w.timestamp || 0;
    if (!ts) return false;
    const d = new Date(ts);
    const dStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return dStr === allData.today;
  });
  return {
    fast: {
      durationMin: fastEvent.duration_min,
      durationLabel: fmtDuration(fastEvent.duration_min),
      tiers: fastEvent.tiers_reached || [],
      starting: fastEvent.starting_meal || {},
      breaking: fastEvent.breaking_meal || {},
    },
    trendContext: summarizeFastsForAI(14),
    todayMacros: {
      cumulative: { calories: cumCal, protein: cumProt, carbs: cumCarb, fat: cumFat },
      goals: {
        calories: allData.calorieGoals?.calories,
        protein:  allData.calorieGoals?.protein,
        carbs:    allData.calorieGoals?.carbs,
        fat:      allData.calorieGoals?.fat,
      },
    },
    recovery: {
      sleepMin: todayLog.sleepMinutes || null,
      restingHr: todayLog.restingHeartRate || null,
      hrv: todayLog.heartRateVariability || null,
    },
    hydrationCupsToday: todayLog.water || 0,
    workoutToday: todayWorkout ? {
      name: todayWorkout.aiName || todayWorkout.name,
      volume: todayWorkout.volume,
      sets: todayWorkout.sets,
    } : null,
    profile: {
      goalDeltaKcal: profile.training.goalDeltaKcal,
      weightTarget: profile.nutrition.weightTarget,
      faithContext: profile.identity.faithContext,
    },
  };
}

const FAST_INSIGHT_SCHEMA = {
  type: "object",
  properties: {
    bullets: {
      type: "array",
      minItems: 4,
      maxItems: 4,
      items: { type: "string" },
    },
  },
  required: ["bullets"],
};

function safeJSON(key, fallback) {
  try { const v = JSON.parse(localStorage.getItem(key) || "null"); return v == null ? fallback : v; }
  catch { return fallback; }
}

async function runGeminiInsight(fastEvent) {
  const ctx = buildFastBreakContext(fastEvent);
  const raw = await withTimeout(
    callGemini(JSON.stringify(ctx), {
      systemInstruction: geminiSystemInstruction(),
      responseSchema: FAST_INSIGHT_SCHEMA,
      model: MODELS.fastInsight,
      maxOutputTokens: 400,
      feature: "fastInsight",
    }),
    GEMINI_TIMEOUT_MS,
  );
  let parsed;
  try { parsed = JSON.parse(raw); }
  catch { return String(raw || "").trim(); }
  const bullets = Array.isArray(parsed?.bullets) ? parsed.bullets.slice(0, 4) : [];
  if (bullets.length === 0) return String(raw || "").trim();
  return bullets.map(b => `• ${String(b || "").trim()}`).join("\n");
}

// Updates the matching row's insight fields and dispatches sirat:fastUpdated.
function patchEventInsight(fastId, insight, source) {
  const events = readEvents();
  const idx = events.findIndex(e => e.id === fastId);
  if (idx === -1) return;
  events[idx] = {
    ...events[idx],
    ai_insight_cached: insight,
    ai_source: source,
    insight_generated_at: new Date().toISOString(),
  };
  writeEvents(events);
  try {
    window.dispatchEvent(new CustomEvent(EVT_UPDATED, { detail: { fast_id: fastId, field: "ai_insight_cached" } }));
  } catch {}
}

// Owns the in-flight Gemini promise — survives toast unmount.
// Resolves with the insight string and writes it back to the row.
export function ensureInsightFor(fastEvent) {
  const fastId = fastEvent.id;
  if (pendingGeminiCalls.has(fastId)) return pendingGeminiCalls.get(fastId);

  const recentDurations = Array.isArray(fastEvent.recent_durations_min)
    ? fastEvent.recent_durations_min
    : [];
  const hasKey = !!getGeminiKey();

  if (!hasKey) {
    const insight = buildRuleInsight(fastEvent, recentDurations);
    patchEventInsight(fastId, insight, "rule");
    console.info("[hayahFastToast] gemini-fallback", { fast_id: fastId, reason: "no-key" });
    return Promise.resolve(insight);
  }

  const p = runGeminiInsight(fastEvent)
    .then(text => {
      patchEventInsight(fastId, text, "gemini");
      console.info("[hayahFastToast] gemini-success", { fast_id: fastId, length: text.length });
      return text;
    })
    .catch(err => {
      const reason = String(err?.message || "").includes("timeout") ? "timeout" : "error";
      const insight = buildRuleInsight(fastEvent, recentDurations);
      patchEventInsight(fastId, insight, "rule");
      console.info("[hayahFastToast] gemini-fallback", { fast_id: fastId, reason });
      return insight;
    })
    .finally(() => {
      pendingGeminiCalls.delete(fastId);
    });

  pendingGeminiCalls.set(fastId, p);
  return p;
}

export async function regenerateInsight(fastId) {
  const fastEvent = getFastById(fastId);
  if (!fastEvent) throw new Error(`No fast event with id ${fastId}`);
  // Drop any cached call for this id so we genuinely re-fetch.
  pendingGeminiCalls.delete(fastId);
  const insight = await ensureInsightFor({ ...fastEvent, ai_insight_cached: null });
  console.info("[hayahFastToast] regenerated", { fast_id: fastId, source: getFastById(fastId)?.ai_source });
  return insight;
}

// ───────────────────────────────────────────────────────────────────
// Mutation API for HayahAICoach. Centralised so the schema contract
// stays in one file and `sirat:fastUpdated` fires automatically.
// ───────────────────────────────────────────────────────────────────

function patchEventRow(fastId, patch, field) {
  const events = readEvents();
  const idx = events.findIndex(e => e.id === fastId);
  if (idx === -1) return false;
  events[idx] = { ...events[idx], ...patch };
  writeEvents(events);
  try {
    window.dispatchEvent(new CustomEvent(EVT_UPDATED, { detail: { fast_id: fastId, field } }));
  } catch {}
  return true;
}

// Set or clear the user's free-text reflection on a fast. Passing null/"" clears
// both the reflection and its timestamp.
export function setUserReflection(fastId, text) {
  const trimmed = text == null ? null : String(text).trim();
  const patch = trimmed
    ? { user_reflection: trimmed, reflection_added_at: new Date().toISOString() }
    : { user_reflection: null, reflection_added_at: null };
  const ok = patchEventRow(fastId, patch, "user_reflection");
  if (ok) console.info("[hayahFastToast] reflection-set", { fast_id: fastId, cleared: !trimmed });
  return ok;
}

// Append tags to a fast event. Deduplicates against existing tags,
// drops empty/non-string values. Returns the final tag list, or null
// if the fast event doesn't exist.
export function addTags(fastId, ...tags) {
  const row = getFastById(fastId);
  if (!row) return null;
  const incoming = tags
    .flat()
    .map(t => String(t || "").trim())
    .filter(Boolean);
  const existing = Array.isArray(row.tags) ? row.tags : [];
  const merged = Array.from(new Set([...existing, ...incoming]));
  if (merged.length === existing.length) return existing;
  patchEventRow(fastId, { tags: merged }, "tags");
  console.info("[hayahFastToast] tags-added", { fast_id: fastId, added: incoming });
  return merged;
}

// Remove tags from a fast event. Silently ignores tags that aren't present.
// Returns the final tag list, or null if the fast event doesn't exist.
export function removeTags(fastId, ...tags) {
  const row = getFastById(fastId);
  if (!row) return null;
  const toDrop = new Set(
    tags.flat().map(t => String(t || "").trim()).filter(Boolean)
  );
  const existing = Array.isArray(row.tags) ? row.tags : [];
  const next = existing.filter(t => !toDrop.has(t));
  if (next.length === existing.length) return existing;
  patchEventRow(fastId, { tags: next }, "tags");
  console.info("[hayahFastToast] tags-removed", { fast_id: fastId, removed: [...toDrop] });
  return next;
}

// ───────────────────────────────────────────────────────────────────
// Auto-tags
// ───────────────────────────────────────────────────────────────────

function autoTagsFor(startTs, endTs, durationMin) {
  const tags = [];
  try {
    const endDate = new Date(endTs);
    const dows = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    tags.push(`dow:${dows[endDate.getDay()]}`);

    const startDate = new Date(startTs);
    const hh = startDate.getHours();
    if (hh >= 21)       tags.push("start_late");
    else if (hh >= 18)  tags.push("start_evening");
    else                tags.push("start_early");

    const top = highestTierSlug(durationMin);
    if (top) tags.push(`tier:${top}`);
  } catch {}
  return tags;
}

// ───────────────────────────────────────────────────────────────────
// Trigger — called from CalorieCounter.addFoodEntry after showToast.
// ───────────────────────────────────────────────────────────────────

function readShown() {
  try {
    const v = JSON.parse(localStorage.getItem(SHOWN_KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}

function appendShown(fastId) {
  try {
    const v = readShown();
    if (!v.includes(fastId)) {
      v.push(fastId);
      const trimmed = v.length > SHOWN_CAP ? v.slice(v.length - SHOWN_CAP) : v;
      localStorage.setItem(SHOWN_KEY, JSON.stringify(trimmed));
    }
  } catch {}
}

function readPending() {
  try { return JSON.parse(localStorage.getItem(PENDING_KEY) || "null"); } catch { return null; }
}

function writePending(v) {
  try {
    if (v == null) localStorage.removeItem(PENDING_KEY);
    else           localStorage.setItem(PENDING_KEY, JSON.stringify(v));
  } catch {}
}

// Recent qualifying-fast durations (most recent 7 ending before `beforeTs`).
function recentQualifyingDurations(beforeTs) {
  return readEvents()
    .filter(e => (e.end_ts || 0) < beforeTs && +e.duration_min > 9 * 60)
    .sort((a, b) => (b.end_ts || 0) - (a.end_ts || 0))
    .slice(0, 7)
    .map(e => +e.duration_min || 0);
}

export function evaluateAndScheduleFastToast(committedLog, entry, routedSlot, foodBase) {
  try {
    if (!committedLog || !entry || !entry.loggedAt) {
      console.info("[hayahFastToast] gate:no-args", { hasLog: !!committedLog, hasEntry: !!entry, hasLoggedAt: !!entry?.loggedAt });
      return;
    }

    const today = localDateStr();
    const loggedAtMs = Date.parse(entry.loggedAt);
    if (!Number.isFinite(loggedAtMs)) {
      console.info("[hayahFastToast] gate:bad-loggedAt", { loggedAt: entry.loggedAt });
      return;
    }

    // (1) Real-time only — back-fills stamp a past time, not "now".
    const age = Date.now() - loggedAtMs;
    if (age > FRESHNESS_MS) {
      console.info("[hayahFastToast] gate:stale", { ageMs: age, freshnessMs: FRESHNESS_MS, loggedAt: entry.loggedAt });
      return;
    }

    // (2) Entry date must be today in local timezone.
    const entryDate = localDateStr(new Date(loggedAtMs));
    if (entryDate !== today) {
      console.info("[hayahFastToast] gate:not-today", { entryDate, today });
      return;
    }

    // Use the SAME fasting settings as HayahToday so the fast window and
    // toleration logic are consistent with what the user sees on screen.
    // A tolerated entry (coffee, water, etc.) does NOT break the fast and
    // does NOT count as the "first meal" for trigger purposes.
    let fastingSettings;
    try {
      const prefs = loadPrefs();
      fastingSettings = {
        tolerance:       prefs.fasting.calorieTolerance,
        allowList:       prefs.fasting.allowList,
        bedtimeOffsetMin: prefs.fasting.bedtimeOffsetMin,
      };
    } catch {
      fastingSettings = { tolerance: 50, allowList: [], bedtimeOffsetMin: 60 };
    }

    // Helper: does an entry count as a fast-break under the user's settings?
    const isTolerated = (m) => {
      const kcal = +m?.calories || 0;
      if (kcal < fastingSettings.tolerance) return true;
      const name = String(m?.name || "").toLowerCase();
      return (fastingSettings.allowList || []).some(a => name.includes(String(a).toLowerCase()));
    };

    // If THIS entry is tolerated (e.g. black coffee, water), it doesn't break
    // the fast — don't trigger the toast for it at all.
    if (isTolerated(entry)) {
      console.info("[hayahFastToast] gate:tolerated-entry", { name: entry.name, calories: entry.calories });
      return;
    }

    // (3) First non-tolerated meal of the day. Tolerated entries (coffee, tea,
    // water) are skipped so they neither trigger nor block the toast.
    const todayMeals = committedLog[today]?.meals || {};
    // Use all stored slot keys + our known slots to catch any slot name.
    const allSlotKeys = Array.from(new Set([...MEAL_SLOTS, ...Object.keys(todayMeals)]));
    for (const slot of allSlotKeys) {
      const arr = Array.isArray(todayMeals[slot]) ? todayMeals[slot] : [];
      for (const m of arr) {
        if (!m || m.id === entry.id) continue;
        if (isTolerated(m)) continue;   // skip tolerated entries
        const ts = Date.parse(m.loggedAt);
        if (Number.isFinite(ts) && ts < loggedAtMs) {
          console.info("[hayahFastToast] gate:not-first-meal", { earlierEntry: m.name, slot, earlierTs: ts, thisTs: loggedAtMs });
          return;
        }
      }
    }

    // (4) Fast window strictly > 9h — same settings the user sees in Hayah.
    const overrides = loadFastingOverrides();
    const fast = computeFastingWindow(committedLog, today, overrides, fastingSettings);
    console.info("[hayahFastToast] fast-window", {
      startTs: fast?.startTs,
      endTs: fast?.endTs,
      hours: fast?.hours,
      inProgress: fast?.inProgress,
      source: fast?.source,
    });
    if (!fast || !Number.isFinite(fast.startTs)) {
      console.info("[hayahFastToast] gate:no-startTs");
      return;
    }
    // endTs may be null if inProgress — use Date.now() as endTs in that case.
    const endTs = Number.isFinite(fast.endTs) ? fast.endTs : Date.now();
    const hours  = fast.hours ?? ((endTs - fast.startTs) / 3600000);
    if (!(hours > 9)) {
      console.info("[hayahFastToast] gate:fast-too-short", { hours });
      return;
    }

    const fastId = `fast_${fast.startTs}`;
    const shown = readShown();
    if (shown.includes(fastId)) {
      console.info("[hayahFastToast] gate:already-shown", { fastId });
      return;
    }
    const pending = readPending();
    if (pending && pending.fast_id === fastId) {
      console.info("[hayahFastToast] gate:already-pending", { fastId });
      return;
    }

    // Find the starting meal by matching fast.startTs against loggedAt in the
    // bucket for that day. Search ALL slot keys to avoid missing 'drinks' or
    // any future slot additions.
    const yKey = localDateStr(new Date(fast.startTs));
    const yMeals = committedLog[yKey]?.meals || {};
    const allYSlotKeys = Array.from(new Set([...MEAL_SLOTS, ...Object.keys(yMeals)]));
    let startingMeal = null;
    for (const slot of allYSlotKeys) {
      const arr = Array.isArray(yMeals[slot]) ? yMeals[slot] : [];
      for (const m of arr) {
        if (Date.parse(m?.loggedAt) === fast.startTs) {
          startingMeal = { id: m.id, name: m.name, slot, calories: +m.calories || 0, source: m.source || null };
          break;
        }
      }
      if (startingMeal) break;
    }
    // Not a hard bail — fast still qualifies even if we can't pin the starting meal
    // (e.g. fast started from sleep-segment estimate rather than a logged entry).
    if (!startingMeal) {
      console.info("[hayahFastToast] warn:no-starting-meal", { yKey, startTs: fast.startTs });
    }

    const durationMin = Math.round(hours * 60);
    const breakingMeal = {
      id: entry.id,
      name: entry.name,
      slot: routedSlot,
      calories: +entry.calories || 0,
      protein:  +entry.protein  || 0,
      carbs:    +entry.carbs    || 0,
      fat:      +entry.fat      || 0,
      sugar:    +entry.sugar    || 0,
      sodium:   +entry.sodium   || 0,
      source:   entry.source    || null,
      category: (foodBase && foodBase.category) || entry.category || null,
    };

    const row = {
      id: fastId,
      schema_version: 1,
      start_ts: fast.startTs,
      end_ts:   endTs,
      duration_min: durationMin,
      starting_meal: startingMeal,
      breaking_meal: breakingMeal,
      tiers_reached: tierSlugsReached(durationMin),
      recent_durations_min: recentQualifyingDurations(fast.startTs),
      ai_insight_cached: null,
      ai_source: null,
      insight_generated_at: null,
      user_reflection: null,
      reflection_added_at: null,
      tags: autoTagsFor(fast.startTs, endTs, durationMin),
      created_at: new Date().toISOString(),
    };

    const events = readEvents();
    // De-dupe on id (defensive — pending/shown guards above already catch this).
    const filtered = events.filter(e => e.id !== fastId);
    filtered.unshift(row);
    writeEvents(filtered);

    try {
      window.dispatchEvent(new CustomEvent(EVT_RECORDED, { detail: { fast_id: fastId, duration_min: durationMin } }));
    } catch {}

    const scheduledFor = Date.now() + TOAST_DELAY_MS;
    writePending({ fast_id: fastId, scheduled_for_ms: scheduledFor });
    console.info("[hayahFastToast] scheduled", { fast_id: fastId, duration_min: durationMin, scheduled_for_ms: scheduledFor });

    setTimeout(() => {
      try { window.dispatchEvent(new CustomEvent(EVT_TOAST, { detail: { fast_id: fastId } })); } catch {}
    }, TOAST_DELAY_MS);
  } catch (err) {
    // Never break the meal-log path.
    try { console.warn("[hayahFastToast] evaluate failed", err); } catch {}
  }
}

// ───────────────────────────────────────────────────────────────────
// Scheduler hook — mounted once in App.js.
// Handles cold-start re-check and forwards events to overlay state.
// ───────────────────────────────────────────────────────────────────

export function useHayahFastToastScheduler() {
  const [event, setEvent] = useState(null);
  const open = !!event;
  const dismissedRef = useRef(false);

  const openFastId = useCallback((fastId) => {
    const row = getFastById(fastId);
    if (!row) {
      // Drop a stale pending entry so we don't keep retrying.
      const pending = readPending();
      if (pending && pending.fast_id === fastId) writePending(null);
      return;
    }
    dismissedRef.current = false;
    setEvent(row);
    console.info("[hayahFastToast] fired", { fast_id: fastId });
  }, []);

  // Subscribe to runtime fire events.
  useEffect(() => {
    const onToast = (e) => {
      const fastId = e?.detail?.fast_id;
      if (!fastId) return;
      const shown = readShown();
      if (shown.includes(fastId)) {
        const pending = readPending();
        if (pending && pending.fast_id === fastId) writePending(null);
        return;
      }
      openFastId(fastId);
    };
    const onUpdated = (e) => {
      const fastId = e?.detail?.fast_id;
      if (!fastId) return;
      // Refresh open event from storage so cached insight populates the UI.
      setEvent(prev => (prev && prev.id === fastId ? getFastById(fastId) || prev : prev));
    };
    window.addEventListener(EVT_TOAST, onToast);
    window.addEventListener(EVT_UPDATED, onUpdated);
    return () => {
      window.removeEventListener(EVT_TOAST, onToast);
      window.removeEventListener(EVT_UPDATED, onUpdated);
    };
  }, [openFastId]);

  // Cold-start re-check: on mount, look at hayahPendingFastToast.
  useEffect(() => {
    const pending = readPending();
    if (!pending || !pending.fast_id) return;
    const shown = readShown();
    if (shown.includes(pending.fast_id)) {
      writePending(null);
      return;
    }
    const delta = (pending.scheduled_for_ms || 0) - Date.now();
    if (delta <= 0) {
      openFastId(pending.fast_id);
    } else {
      const to = setTimeout(() => {
        try { window.dispatchEvent(new CustomEvent(EVT_TOAST, { detail: { fast_id: pending.fast_id } })); } catch {}
      }, delta);
      return () => clearTimeout(to);
    }
  }, [openFastId]);

  const dismiss = useCallback(() => {
    if (!event || dismissedRef.current) return;
    dismissedRef.current = true;
    appendShown(event.id);
    writePending(null);
    console.info("[hayahFastToast] dismissed", { fast_id: event.id });
    setEvent(null);
  }, [event]);

  return { open, event, dismiss };
}

// ───────────────────────────────────────────────────────────────────
// Debug helpers — exposed on window.__siratDebug so they can be
// called from Safari Web Inspector console on device/simulator.
// Not tree-shaken in prod but harmless — no UI surface.
// ───────────────────────────────────────────────────────────────────

if (typeof window !== "undefined") {
  const dbg = window.__siratDebug || (window.__siratDebug = {});

  /**
   * Inject a fake fast event and fire the toast immediately (no 20s delay).
   *
   * Usage from Safari Web Inspector console:
   *   __siratDebug.simulateFastToast()         // defaults to 14h fast
   *   __siratDebug.simulateFastToast(16.5)     // 16h30m fast
   *   __siratDebug.simulateFastToast(0, true)  // wipe hayahShownFastToasts first
   *
   * To reset so you can fire it again:
   *   localStorage.removeItem('hayahPendingFastToast');
   *   localStorage.removeItem('hayahShownFastToasts');
   */
  dbg.simulateFastToast = function simulateFastToast(hours = 14, wipeShown = false) {
    if (wipeShown) {
      localStorage.removeItem(SHOWN_KEY);
      localStorage.removeItem(PENDING_KEY);
      console.info("[hayahFastToast] debug: wiped shown + pending lists");
    }

    const nowMs = Date.now();
    const startTs = nowMs - Math.round(hours * 3600 * 1000);
    const endTs   = nowMs;
    const durationMin = Math.round(hours * 60);
    const fastId  = `fast_debug_${startTs}`;

    const row = {
      id: fastId,
      schema_version: 1,
      start_ts: startTs,
      end_ts:   endTs,
      duration_min: durationMin,
      starting_meal: {
        id: "debug_start",
        name: "Debug start meal",
        slot: "meal",
        calories: 650,
        source: null,
      },
      breaking_meal: {
        id: "debug_break",
        name: "Debug breaking meal",
        slot: "meal",
        calories: 420,
        protein: 32,
        carbs: 45,
        fat: 12,
        sugar: 8,
        sodium: 380,
        source: null,
        category: null,
      },
      tiers_reached: tierSlugsReached(durationMin),
      recent_durations_min: [],
      ai_insight_cached: null,
      ai_source: null,
      insight_generated_at: null,
      user_reflection: null,
      reflection_added_at: null,
      tags: autoTagsFor(startTs, endTs, durationMin),
      created_at: new Date().toISOString(),
    };

    // Persist the row.
    const events = readEvents().filter(e => e.id !== fastId);
    events.unshift(row);
    writeEvents(events);

    // Clear stale pending for this id and write a fresh one.
    writePending({ fast_id: fastId, scheduled_for_ms: Date.now() });

    // Fire immediately.
    try {
      window.dispatchEvent(new CustomEvent(EVT_TOAST, { detail: { fast_id: fastId } }));
    } catch {}

    console.info("[hayahFastToast] debug: simulateFastToast fired", {
      fastId, hours, durationMin, tiers: row.tiers_reached,
    });
    return row;
  };

  /** Reset everything so you can re-simulate: wipe pending + shown lists. */
  dbg.resetFastToast = function resetFastToast() {
    localStorage.removeItem(PENDING_KEY);
    localStorage.removeItem(SHOWN_KEY);
    console.info("[hayahFastToast] debug: reset complete — call simulateFastToast() again");
  };

  /** Dump the last 5 fast events from localStorage. */
  dbg.inspectFasts = function inspectFasts(limit = 5) {
    const fasts = getRecentFasts(limit);
    console.table(fasts.map(f => ({
      id: f.id,
      duration: fmtDuration(f.duration_min),
      tiers: (f.tiers_reached || []).join(", "),
      breaking: f.breaking_meal?.name,
      insight: f.ai_insight_cached ? f.ai_insight_cached.slice(0, 60) + "…" : "—",
    })));
    return fasts;
  };

  /**
   * Run the REAL gate logic (evaluateAndScheduleFastToast) with a fake
   * "first real meal of the day" entry built from the current calorieLog.
   * Reads calorieLog from localStorage so injected test data works.
   * Fires the toast after TOAST_DELAY_MS (20s) if all gates pass.
   *
   * Usage:
   *   __siratDebug.testFastGates()           // fake 500 kcal chicken entry
   *   __siratDebug.testFastGates('Pizza', 'snacks', 800)
   */
  dbg.testFastGates = function testFastGates(name = "Test Meal (Grilled Chicken)", slot = "meal", calories = 480) {
    const log = safeJSON("calorieLog", {});
    const today = localDateStr();
    const now = new Date();
    const entry = {
      id: `dbg_gate_${Date.now()}`,
      name,
      calories,
      protein: 38,
      carbs: 42,
      fat: 12,
      sugar: 5,
      sodium: 430,
      fiber: 3,
      servingG: 300,
      servingLabel: "1 plate",
      source: "sunnah",
      loggedAt: now.toISOString(),
    };
    // Commit entry to the log (mimics what addFoodEntry does).
    const prev = log[today] || { meals: {}, water: 0, weight: null };
    const committedLog = {
      ...log,
      [today]: {
        ...prev,
        meals: { ...(prev.meals || {}), [slot]: [...((prev.meals || {})[slot] || []), entry] },
      },
    };
    try { localStorage.setItem("calorieLog", JSON.stringify(committedLog)); } catch {}
    console.info("[hayahFastToast] debug: testFastGates — calling evaluateAndScheduleFastToast with", { name, slot, calories, loggedAt: entry.loggedAt });
    evaluateAndScheduleFastToast(committedLog, entry, slot, { name, calories, source: "sunnah" });
    console.info("[hayahFastToast] debug: testFastGates — if 'scheduled' logged above, toast fires in 20s; check hayahPendingFastToast:", safeJSON("hayahPendingFastToast", null));
  };
}
